import { darkTheme, classicTheme } from '../../../theme'
import { getCookie } from '../../../shared/lib/formatter/get-cookie'
import { createPersistedStore } from '../../../shared/stores/create-persisted-store'
import { storage } from '../../../shared/storage'

export interface ThemeState {
  theme: Record<string, string>
  themeName: string
  setTheme: (theme: Record<string, string>) => void
  setThemeName: (name: string) => void
}

const themeMap: Record<string, Record<string, string>> = {
  dark: darkTheme as Record<string, string>,
  classic: classicTheme as Record<string, string>,
}

function getInitialThemeName(): string {
  return storage.prefs.getTheme() || getCookie('themeName') || 'dark'
}

function getInitialTheme(): Record<string, string> {
  return themeMap[getInitialThemeName()] || (darkTheme as Record<string, string>)
}

/**
 * Maps camelCase theme keys to CSS custom property names.
 * e.g. "sidebarBackground" → "--sidebar-bg"
 */
const themeKeyToCssVar: Record<string, string> = {
  mainTitleColor: '--main-title-color',
  mainDescriptionColor: '--main-description-color',
  background: '--background',
  mainPageBg: '--main-page-bg',
  sidebarBackground: '--sidebar-bg',
  sidebarGroupBackground: '--sidebar-group-bg',
  sidebarGroupTextColor: '--sidebar-group-text',
  sidebarTextColor: '--sidebar-text',
  sidebarItemActive: '--sidebar-item-active',
  sidebarItemHover: '--sidebar-item-hover',
  sidebarItemActiveBlur: '--sidebar-item-active-blur',
  formBackground: '--form-bg',
  formHeaderTitleColor: '--form-header-title',
  formHeaderSubtitleColor: '--form-header-subtitle',
  primaryButtonBackground: '--primary-btn-bg',
  primaryButtonText: '--primary-btn-text',
  primaryButtonHoverBackground: '--primary-btn-hover-bg',
  primaryButtonLightBackground: '--primary-btn-light-bg',
  primaryButtonLightText: '--primary-btn-light-text',
  primaryButtonTextHover: '--primary-btn-text-hover',
  primaryButtonBorder: '--primary-btn-border',
  primaryDisabledTextColor: '--primary-disabled-text',
  secondaryButtonBackground: '--secondary-btn-bg',
  secondaryButtonText: '--secondary-btn-text',
  secondaryButtonHoverBackground: '--secondary-btn-hover-bg',
  secondaryButtonTextHover: '--secondary-btn-text-hover',
  secondaryButtonBorder: '--secondary-btn-border',
  cancelButtonBackground: '--cancel-btn-bg',
  cancelButtonText: '--cancel-btn-text',
  cancelButtonHoverBackground: '--cancel-btn-hover-bg',
  cancelButtonTextHover: '--cancel-btn-text-hover',
  cancelButtonBorder: '--cancel-btn-border',
  primaryInputBorder: '--input-border',
  primaryInputBorderHover: '--input-border-hover',
  primaryInputBorderFocus: '--input-border-focus',
  primaryBorderColor: '--primary-border-color',
  segmentedBackground: '--segmented-bg',
  segmentedActiveText: '--segmented-active-text',
  subHeaderBackground: '--sub-header-bg',
  contentMainTitleColor: '--content-main-title',
  contentMainDescriptionColor: '--content-main-description',
  linkTitleColor: '--link-title-color',
  tableLabelColor: '--table-label-color',
  tableValueColor: '--table-value-color',
  mainIconColor: '--icon-main',
  defaultIconColor: '--icon-default',
  warningIconColor: '--icon-warning',
  successIconColor: '--icon-success',
  infoIconColor: '--icon-info',
  errorIconColor: '--icon-error',
  secondaryIconColor: '--icon-secondary',
  defaultTextColor: '--text-default',
  warningTextColor: '--text-warning',
  successTextColor: '--text-success',
  infoTextColor: '--text-info',
  errorTextColor: '--text-error',
  secondaryTextColor: '--text-secondary',
  pageHeaderBackground: '--page-header-bg',
  pageHeaderTitleColor: '--page-header-title-color',
  pageHeaderTitleFontSize: '--page-header-title-size',
  pageHeaderTitleFontWeight: '--page-header-title-weight',
  pageHeaderSubtitleColor: '--page-header-subtitle-color',
  pageHeaderSubtitleFontSize: '--page-header-subtitle-size',
  pageHeaderSubtitleFontWeight: '--page-header-subtitle-weight',
  scrollBarThumb: '--scrollbar-thumb',
  scrollBarBackground: '--scrollbar-bg',
  mainPageActiveShortcutButtonBackground: '--shortcut-active-bg',
  mainPageActiveShortcutButtonText: '--shortcut-active-text',
  mainPageInactiveShortcutButtonBackground: '--shortcut-inactive-bg',
  mainPageInactiveShortcutButtonText: '--shortcut-inactive-text',
  warehouseCardBackground: '--warehouse-card-bg',
  warehouseCardBorder: '--warehouse-card-border',
  warehouseCardBackgroundHover: '--warehouse-card-bg-hover',
  warehouseCardBorderHover: '--warehouse-card-border-hover',
  tabContainerBackground: '--tab-container-bg',
  tabBackground: '--tab-bg',
  tabText: '--tab-text',
  tabIcon: '--tab-icon',
  tabSelectedBackground: '--tab-selected-bg',
  tabIconSelected: '--tab-icon-selected',
  tabSelectedText: '--tab-selected-text',
  // Semantic surface tokens (theme-variables.css "semantic surface" section)
  surfaceStrong: '--surface-strong',
  surfaceSoft: '--surface-soft',
  surfaceSofter: '--surface-softer',
  textOnSurface: '--text-on-surface',
  textMuted: '--text-muted',
  borderSubtle: '--border-subtle',
  borderStrong: '--border-strong',
  accentLink: '--accent-link',
  statePositive: '--state-positive',
  stateWarning: '--state-warning',
  stateCritical: '--state-critical',
  stateInfo: '--state-info',
  textOnSurfaceSecondary: '--text-on-surface-secondary',
  textMutedSoft: '--text-muted-soft',
  surfaceMuted: '--surface-muted',
  // Status tint backgrounds
  warningTint: '--tint-warning',
  successTint: '--tint-success',
  infoTint: '--tint-info',
  primaryTint: '--tint-primary',
  // Accounting category identity palette
  categoryIncomeProduction: '--cat-income-prod',
  categoryIncomeProductionTint: '--cat-income-prod-tint',
  categoryIncomeService: '--cat-income-srv',
  categoryIncomeServiceTint: '--cat-income-srv-tint',
  categoryIncomeOther: '--cat-income-other',
  categoryIncomeOtherTint: '--cat-income-other-tint',
  categoryIncomeLoan: '--cat-income-loan',
  categoryIncomeLoanTint: '--cat-income-loan-tint',
  categoryExpenseMaterial: '--cat-expense-mat',
  categoryExpenseMaterialTint: '--cat-expense-mat-tint',
  categoryExpenseTransport: '--cat-expense-trans',
  categoryExpenseTransportTint: '--cat-expense-trans-tint',
  categoryExpenseSalary: '--cat-expense-zp',
  categoryExpenseSalaryTint: '--cat-expense-zp-tint',
  categoryExpenseUtility: '--cat-expense-util',
  categoryExpenseUtilityTint: '--cat-expense-util-tint',
  categoryExpenseRent: '--cat-expense-rent',
  categoryExpenseRentTint: '--cat-expense-rent-tint',
  categoryExpenseTax: '--cat-expense-tax',
  categoryExpenseTaxTint: '--cat-expense-tax-tint',
  categoryExpenseOther: '--cat-expense-other',
  categoryExpenseOtherTint: '--cat-expense-other-tint',
  navigationTextColor: '--navigation-text-color',
  warehouseCardTitle: '--warehouse-card-title',
  columnBackground: '--column-bg',
  buttonColor: '--button-color',
  buttonHoverBackground: '--button-hover-bg',
  progressBarBackground: '--progress-bar-bg',
  modalBackground: '--modal-bg',
  subHeaderTitle: '--sub-header-title',
  subHeaderDescription: '--sub-header-description',
  captionColor: '--caption-color',
}

/** Sync theme object properties to CSS custom properties on :root */
function syncThemeToCssVars(theme: Record<string, string>) {
  const root = document.documentElement
  for (const [key, cssVar] of Object.entries(themeKeyToCssVar)) {
    if (key in theme) {
      root.style.setProperty(cssVar, String(theme[key]))
    }
  }
}

export const useThemeStore = createPersistedStore<
  ThemeState,
  Pick<ThemeState, 'theme' | 'themeName'>
>(
  (set) => ({
    theme: getInitialTheme(),
    themeName: getInitialThemeName(),
    setTheme: (theme) =>
      set((state) => {
        state.theme = theme
        syncThemeToCssVars(theme)
      }),
    setThemeName: (name) =>
      set((state) => {
        state.themeName = name
      }),
  }),
  {
    name: 'glasspro:theme',
    version: 1,
    // Tokens + name are user-facing; methods are recreated by the initializer.
    partialize: (state) => ({ theme: state.theme, themeName: state.themeName }),
    // After hydration from localStorage, push the loaded tokens into CSS
    // custom properties on :root — otherwise the DOM renders with the
    // initial-load defaults until a setTheme() is fired.
    onRehydrateStorage: () => (hydrated) => {
      if (hydrated?.theme) syncThemeToCssVars(hydrated.theme)
    },
  },
)
